<script setup>
import {ref} from "vue";
import { onBeforeUnmount } from 'vue';
import {createStream} from "@/services/stream.service";
import useMarkdown from "@/composables/useMarkdown";
import elasticAiConversationsService from "@/services/elastic-ai-conversations.service";
import ElasticChatStartup from "@/views/search/ai/ElasticChatStartup.vue";

const md = useMarkdown();

const question = ref("");
const loading = ref(false);
const stream = ref(null);
const rounds = ref([]);
const conversationId = ref('');
const systemMessages = ref([]);
const loaderMessage = ref('');
const content = ref(null);

function clearStream() {
    if (stream.value) {
        stream.value.close();
        stream.value = null;
    }
}

function sendRequest() {
    if (!question.value.trim()) {
        return;
    }

    // close previous stream if still open to avoid multiple concurrent connections
    clearStream();

    const params = new URLSearchParams();

    params.set('question', btoa(unescape(encodeURIComponent(question.value))));

    if (conversationId.value?.length) {
        params.set('conversation_id', btoa(unescape(encodeURIComponent(conversationId.value))));
    }

    const safeUrl = '/chat-stream/get-elastic-ai-response?' + params.toString();
    stream.value = createStream(safeUrl);

    let round = {
        question: question.value,
        answer: '',
        follow_up: '',
        parts: [],
    };

    rounds.value.push(round);
    question.value = '';
    loading.value = true;

    stream.value.onerror = (err) => {
        round.answer = 'SSE connection error. Please try again later.';
        console.error('SSE error', err);
        clearStream();

        // stop loading if connection fails
        loading.value = false;
    };

    stream.value.onmessage = function(event) {
        loaderMessage.value = '';
        round.answer += event.data;
    };

    stream.value.addEventListener('system', function(event) {
        systemMessages.value.push(event.data);
    });

    stream.value.addEventListener('loader', function(event) {
        loaderMessage.value = event.data;
    });

    stream.value.addEventListener('done', function(event) {
        const data = JSON.parse(event.data);

        loaderMessage.value = '';
        loading.value = false;

        round.parts = data.parts;
        round.follow_up = data.follow_up;
        conversationId.value = data.conversation_id;

        stream.value.close();
        stream.value = null;
    });

    requestAnimationFrame(() => {
        content.value.scrollTop = content.value.scrollHeight;
    });
}

onBeforeUnmount(() => {
    if (stream.value) {
        try { stream.value.close(); } catch (e) {
            /* ignore */
        }
        stream.value = null;
    }
});

async function openConversation(id) {
    rounds.value = [];
    const {data} = await elasticAiConversationsService.getConversationData(id);

    rounds.value = data.rounds;
    conversationId.value = data.conversation_id;
}

function startChat(startQuestion) {
    question.value = startQuestion;
    sendRequest();
}

defineExpose({
    startChat,
    openConversation
});
</script>

<template>
    <div class="modal-ai-chat__content" ref="content">
        <div class="ai-chat__inner">
            <div
                v-for="(round, index) in rounds"
                :key="index"
                class="ai-chat"
            >
            
                <div class="ai-chat__user">
                    {{round.question}}
                </div>

                <div class="ai-chat__machine" :class="{'long-div': loading && (index === rounds.length - 1)}">
                    <div v-if="loaderMessage.length && index === rounds.length -1">
                        <div class="ai-loader">
                            <span>{{loaderMessage}}</span>
                            <span class="ai-loader__circle ml-2"></span>
                            <span class="ai-loader__circle"></span>
                            <span class="ai-loader__circle"></span>
                        </div>
                    </div>

                    <template v-if="round.parts.length">
                        <div class="list-result">
                            <div v-for="(part, partIndex) in round.parts" :key="partIndex">
                                <template v-if="part.type === 'startup'">
                                    <div
                                        v-if="part.data"
                                        >

                                        <ElasticChatStartup
                                            :key="part.id"
                                            :startup="part.data"
                                        />
                                    </div>
                                </template>

                                <div v-else v-html="md.render(part.text)"/>
                            </div>
                        </div>
                    </template>

                    <div v-else v-html="md.render(round.answer)"></div>
                </div>

                <div v-if="round.follow_up" v-html="md.render(round.follow_up)"></div>
            </div>

            <div class="ai-chat__footer">
                <div v-if="rounds.length" class="card--ai__search">
                    <input
                        v-focus
                        v-model="question"
                        :disabled="loading"
                        placeholder="Ask another question"
                        class="card--ai__search__input"
                        @keyup.enter="sendRequest"
                    >

                    <button :disabled="loading" class="card--ai__search__button" @click="sendRequest"/>
                </div>

                <div class="ai-chat__footer__hidden-button">
                    <b-button v-b-toggle.messages variant="primary">System Messages ({{systemMessages.length}})</b-button>
                    <b-collapse id="messages" class="mt-2">
                        <div>
                            Conversation ID: {{conversationId}}
                        </div>

                        <div v-for="(message, index) in systemMessages" :key="index">
                            {{ message }}
                        </div>

                        <a class="link" @click="systemMessages = []">Clear</a>
                    </b-collapse>

                    <br><br>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.long-div {
    min-height: 140px;
}

.modal-ai-chat__content {
    overflow-y: auto;  
     /* REQUIRED */
    min-height: 0;
    scroll-behavior: smooth;
}
</style>
